import { supabase } from "@/integrations/supabase/client";

interface VNNOXListParams {
  count?: number;
  start?: number;
  name?: string;
}

interface VNNOXHeaders {
  [key: string]: string;
  appkey: string;
  nonce: string;
  curtime: string;
  checksum: string;
  'Content-Type': string;
  Accept: string;
}

interface VNNOXCredentials {
  id: string;
  app_key: string;
  app_secret: string;
  domain: string;
  description: string;
  is_active: boolean;
}

interface VNNOXRawPlayer {
  id: string;
  name: string;
  status: string;
  onlineStatus: number; // 0 = offline, 1 = online
  lastHeartbeat: string;
  version?: string;
  ip?: string;
  type?: number;
}

interface VNNOXResponse {
  code: number;
  msg: string;
  total: number;
  rows: VNNOXRawPlayer[];
}

export class VNNOXService {
  private baseUrl: string;
  private appKey: string | null;
  private appSecret: string | null;
  private domain: string | null;

  constructor() {
    // En desarrollo usamos el proxy de Vite, en producción el proxy PHP
    this.baseUrl = import.meta.env.DEV 
      ? '/api/vnnox'  // Proxy de Vite
      : '/api/vnnox/proxy.php';  // Proxy PHP
    this.appKey = null;
    this.appSecret = null;
    this.domain = null;
  }

  async loadCredentials(): Promise<boolean> {
    try {
      const { data: credentials, error } = await supabase
        .from('vnnox_credentials')
        .select('*')
        .eq('is_active', true)
        .single();

      if (error) {
        console.error('Error al cargar credenciales:', error);
        return false;
      }

      if (!credentials) {
        console.error('No se encontraron credenciales activas');
        return false;
      }

      this.appKey = credentials.app_key;
      this.appSecret = credentials.app_secret;
      this.domain = credentials.domain;

      return true;
    } catch (error) {
      console.error('Error inesperado al cargar credenciales:', error);
      return false;
    }
  }

  private async generateAuthHeaders(): Promise<VNNOXHeaders> {
    if (!this.appKey || !this.appSecret) {
      const loaded = await this.loadCredentials();
      if (!loaded) {
        throw new Error('No hay credenciales de VNNOX configuradas');
      }
    }

    const nonce = Math.random().toString(36).substring(2, 10);
    const curTime = Math.floor(Date.now() / 1000).toString();
    
    const mensaje = this.appSecret + nonce + curTime;
    const msgBuffer = new TextEncoder().encode(mensaje);
    const hashBuffer = await crypto.subtle.digest('SHA-256', msgBuffer);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    const checkSum = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');

    return {
      appkey: this.appKey!,
      nonce: nonce,
      curtime: curTime,
      checksum: checkSum,
      'Content-Type': 'application/json',
      Accept: 'application/json'
    };
  }

  async getPlayers(params: VNNOXListParams = {}): Promise<VNNOXResponse> {
    try {
      if (!this.appKey || !this.appSecret) {
        const loaded = await this.loadCredentials();
        if (!loaded) {
          throw new Error('No hay credenciales de VNNOX configuradas');
        }
      }

      const queryParams = new URLSearchParams();
      
      const count = params.count ? Math.min(Math.max(params.count, 1), 100) : 20;
      queryParams.append('count', count.toString());
      
      const start = params.start || 0;
      queryParams.append('start', start.toString());
      
      if (params.name) {
        queryParams.append('name', params.name);
      }

      // Construir la URL según el entorno
      let url = this.baseUrl;
      if (import.meta.env.DEV) {
        url += '/v2/player/list';  // En desarrollo, la ruta completa
      } else {
        url += '/v2/player/list';  // En producción, el proxy PHP maneja la ruta
      }
      url += `?${queryParams.toString()}`;

      console.log('Realizando solicitud GET a:', url);
      const headers = await this.generateAuthHeaders();
      console.log('Headers:', {
        ...headers,
        appkey: headers.appkey.substring(0, 8) + '...',
        checksum: headers.checksum.substring(0, 8) + '...'
      });

      const response = await fetch(url, {
        method: 'GET',
        headers: headers,
        credentials: 'same-origin'
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Error en la respuesta:', {
          status: response.status,
          statusText: response.statusText,
          error: errorText,
          headers: Object.fromEntries(response.headers)
        });
        throw new Error(`Error al obtener reproductores: ${response.status} - ${errorText || response.statusText}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error al obtener reproductores:', error);
      if (error instanceof Error) {
        throw error;
      }
      throw new Error('No se pudo establecer conexión con el servidor VNNOX. Verifica tu conexión a internet y que el servidor esté disponible.');
    }
  }

  // Métodos para gestionar credenciales
  async getAllCredentials(): Promise<VNNOXCredentials[]> {
    const { data, error } = await supabase
      .from('vnnox_credentials')
      .select('*')
      .order('created_at', { ascending: false });

    if (error) throw error;
    return data;
  }

  async addCredentials(appKey: string, appSecret: string, domain: string, description: string): Promise<void> {
    const { error } = await supabase
      .from('vnnox_credentials')
      .insert([
        { 
          app_key: appKey, 
          app_secret: appSecret, 
          domain: domain,
          description, 
          is_active: false 
        }
      ]);

    if (error) throw error;
  }

  async setActiveCredentials(id: string): Promise<void> {
    // Primero, desactivamos todas las credenciales
    await supabase
      .from('vnnox_credentials')
      .update({ is_active: false })
      .neq('id', id);

    // Luego, activamos las credenciales seleccionadas
    const { error } = await supabase
      .from('vnnox_credentials')
      .update({ is_active: true })
      .eq('id', id);

    if (error) throw error;

    // Recargamos las credenciales en el servicio
    await this.loadCredentials();
  }

  async deleteCredentials(id: string): Promise<void> {
    const { error } = await supabase
      .from('vnnox_credentials')
      .delete()
      .eq('id', id);

    if (error) throw error;
  }
}

// Exportar una instancia única del servicio
export const vnnoxService = new VNNOXService();
